import { useEffect, useRef, useCallback, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import type {
  RushStartedEvent,
  DealClaimedEvent,
  DealFullyClaimedEvent,
  MetricsUpdatedEvent,
} from '@run2sale/shared';

const SOCKET_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

type MerchantEventMap = {
  'rush:started': RushStartedEvent;
  'deal:claimed': DealClaimedEvent;
  'deal:fully-claimed': DealFullyClaimedEvent;
  'metrics:updated': MetricsUpdatedEvent;
};

type MerchantEventHandler<K extends keyof MerchantEventMap> = (
  payload: MerchantEventMap[K]
) => void;

export function useMerchantSocket(storeId: string | undefined) {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const handlersRef = useRef<Map<string, Set<MerchantEventHandler<keyof MerchantEventMap>>>>(
    new Map()
  );

  useEffect(() => {
    if (!storeId) return;

    const token = localStorage.getItem('auth_token');
    const socket = io(`${SOCKET_URL}/merchant`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      socket.emit('subscribe:store', { storeId });
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    // Forward all merchant events to registered handlers
    const events: (keyof MerchantEventMap)[] = [
      'rush:started',
      'deal:claimed',
      'deal:fully-claimed',
      'metrics:updated',
    ];

    events.forEach((event) => {
      socket.on(event, (payload: MerchantEventMap[typeof event]) => {
        const handlers = handlersRef.current.get(event);
        if (handlers) {
          handlers.forEach((handler) => handler(payload));
        }
      });
    });

    return () => {
      socket.emit('unsubscribe:store', { storeId });
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [storeId]);

  const on = useCallback(
    <K extends keyof MerchantEventMap>(event: K, handler: MerchantEventHandler<K>) => {
      if (!handlersRef.current.has(event)) {
        handlersRef.current.set(event, new Set());
      }
      handlersRef.current.get(event)!.add(handler as MerchantEventHandler<keyof MerchantEventMap>);

      return () => {
        handlersRef.current
          .get(event)
          ?.delete(handler as MerchantEventHandler<keyof MerchantEventMap>);
      };
    },
    []
  );

  return { isConnected, on };
}
